<template>
  <div class="audiencia-page">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <p class="text-muted mb-0">
          Gerencie e segmente seus contatos para campanhas direcionadas
        </p>
      </div>
      <b-button
        variant="primary"
        @click="abrirModalTipoAudiencia"
      >
        <i class="feather icon-plus mr-2" />
        Criar Nova Audiência
      </b-button>
    </div>

    <!-- Seletor de Loja (aparece apenas se tiver mais de uma) -->
    <b-row
      v-if="stores.length > 1"
      class="mb-4 justify-content-center"
    >
      <b-col
        cols="12"
        md="6"
        lg="4"
      >
        <div class="store-selector text-center">
          <label class="form-label mb-1 text-muted small">Selecionar Loja:</label>
          <b-form-select
            v-model="storeUuid"
            :options="storeOptions"
            size="sm"
            @change="onStoreChange"
          >
            <template #first>
              <b-form-select-option
                :value="''"
                disabled
              >
                Escolha uma loja...
              </b-form-select-option>
            </template>
          </b-form-select>
        </div>
      </b-col>
    </b-row>

    <!-- Estatísticas -->
    <b-row class="mb-4">
      <b-col md="3">
        <b-card class="text-center border-0 shadow-sm">
          <b-card-body>
            <div class="text-primary mb-2">
              <i
                class="feather icon-users"
                style="font-size: 2rem;"
              />
            </div>
            <h4 class="mb-1">
              {{ estatisticas.totalAudiencias }}
            </h4>
            <p class="text-muted mb-0">
              Audiências Criadas
            </p>
          </b-card-body>
        </b-card>
      </b-col>

      <b-col md="3">
        <b-card class="text-center border-0 shadow-sm">
          <b-card-body>
            <div class="text-success mb-2">
              <i
                class="feather icon-user-check"
                style="font-size: 2rem;"
              />
            </div>
            <h4 class="mb-1">
              {{ estatisticas.totalContatos }}
            </h4>
            <p class="text-muted mb-0">
              Total de Contatos
            </p>
          </b-card-body>
        </b-card>
      </b-col>

      <b-col md="3">
        <b-card class="text-center border-0 shadow-sm">
          <b-card-body>
            <div class="text-warning mb-2">
              <i
                class="feather icon-target"
                style="font-size: 2rem;"
              />
            </div>
            <h4 class="mb-1">
              {{ estatisticas.audienciasAtivas }}
            </h4>
            <p class="text-muted mb-0">
              Audiências Ativas
            </p>
          </b-card-body>
        </b-card>
      </b-col>

      <b-col md="3">
        <b-card class="text-center border-0 shadow-sm">
          <b-card-body>
            <div class="text-info mb-2">
              <i
                class="feather icon-activity"
                style="font-size: 2rem;"
              />
            </div>
            <h4 class="mb-1">
              {{ estatisticas.criadasHoje }}
            </h4>
            <p class="text-muted mb-0">
              Criadas Hoje
            </p>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Lista de Audiências -->
    <b-card>
      <b-card-header class="d-flex justify-content-between align-items-center">
        <h5 class="mb-0">
          Suas Audiências
        </h5>
        <div class="d-flex align-items-center">
          <b-form-input
            v-model="filtroNome"
            placeholder="Buscar audiência..."
            size="sm"
            style="width: 200px;"
            class="mr-2"
          />
          <b-button
            variant="outline-secondary"
            size="sm"
            @click="carregarAudiencias"
          >
            <i class="feather icon-refresh-cw" />
          </b-button>
        </div>
      </b-card-header>

      <b-card-body>
        <!-- Loading -->
        <div
          v-if="loading"
          class="text-center py-4"
        >
          <b-spinner class="mb-2" />
          <p class="text-muted">
            Carregando audiências...
          </p>
        </div>

        <!-- Estado vazio -->
        <div
          v-else-if="audienciasFiltradas.length === 0 && !loading"
          class="text-center py-5"
        >
          <div class="mb-3">
            <i
              class="feather icon-users text-muted"
              style="font-size: 4rem;"
            />
          </div>
          <h5 class="text-muted">
            {{ audiencias.length === 0 ? 'Nenhuma audiência criada' : 'Nenhuma audiência encontrada' }}
          </h5>
          <p class="text-muted mb-4">
            {{ audiencias.length === 0
              ? 'Crie sua primeira audiência para segmentar seus contatos e melhorar suas campanhas.'
              : 'Tente ajustar o filtro de busca para encontrar a audiência desejada.'
            }}
          </p>
          <b-button
            v-if="audiencias.length === 0"
            variant="primary"
            @click="abrirModalTipoAudiencia"
          >
            <i class="feather icon-plus mr-2" />
            Criar Primeira Audiência
          </b-button>
        </div>

        <!-- Lista de audiências -->
        <div v-else>
          <b-row>
            <b-col
              v-for="audiencia in audienciasFiltradas"
              :key="audiencia.id"
              md="6"
              lg="4"
              class="mb-4"
            >
              <b-card class="h-100 shadow-sm hover-card">
                <b-card-body class="d-flex flex-column">
                  <div class="d-flex justify-content-between align-items-start mb-3">
                    <div>
                      <h6 class="mb-1 font-weight-bold">
                        {{ audiencia.name }}
                      </h6>
                      <small class="text-muted">{{ audiencia.description || 'Sem descrição' }}</small>
                    </div>
                    <b-badge :variant="audiencia.is_active ? 'success' : 'secondary'">
                      {{ audiencia.is_active ? 'Ativa' : 'Inativa' }}
                    </b-badge>
                  </div>

                  <div class="mb-3 flex-grow-1">
                    <div class="d-flex justify-content-between mb-2">
                      <span class="text-muted">Contatos:</span>
                      <strong>{{ audiencia.contact_count || audiencia.total_contacts || 0 }}</strong>
                    </div>
                    <div class="d-flex justify-content-between mb-2">
                      <span class="text-muted">Criada em:</span>
                      <small>{{ formatarData(audiencia.created_at) }}</small>
                    </div>
                    <div class="d-flex justify-content-between">
                      <span class="text-muted">Última atualização:</span>
                      <small>{{ formatarData(audiencia.updated_at) }}</small>
                    </div>
                  </div>

                  <!-- Indicador de Status -->
                  <div class="mb-3">
                    <!-- Status: Processing -->
                    <b-alert
                      v-if="audiencia.status === 'processing'"
                      show
                      variant="warning"
                      class="mb-0 py-2 px-3"
                    >
                      <div class="d-flex align-items-center">
                        <b-spinner
                          small
                          class="mr-2"
                        />
                        <span class="small">Processando a audiência...</span>
                      </div>
                    </b-alert>

                    <!-- Status: Error -->
                    <b-alert
                      v-else-if="audiencia.status === 'error'"
                      show
                      variant="danger"
                      class="mb-0 py-2 px-3"
                    >
                      <div class="d-flex align-items-center">
                        <i class="feather icon-alert-circle mr-2" />
                        <span class="small">Ocorreu um erro ao salvar a audiência</span>
                      </div>
                    </b-alert>

                    <!-- Status: Success -->
                    <b-alert
                      v-else-if="audiencia.status === 'success'"
                      show
                      variant="success"
                      class="mb-0 py-2 px-3"
                    >
                      <div class="d-flex align-items-center">
                        <i class="feather icon-check-circle mr-2" />
                        <span class="small">Audiência pronta para visualização</span>
                      </div>
                    </b-alert>
                  </div>

                  <div class="mt-auto">
                    <div class="d-flex align-items-center">
                      <b-button
                        v-if="audiencia.status !== 'error'"
                        variant="outline-primary"
                        size="sm"
                        class="flex-grow-1 mr-2"
                        :disabled="audiencia.status !== 'success'"
                        @click="visualizarAudiencia(audiencia)"
                      >
                        <i class="feather icon-eye mr-2" />
                        Visualizar Audiência
                      </b-button>
                      <b-button
                        v-else
                        variant="outline-danger"
                        size="sm"
                        class="flex-grow-1 mr-2"
                        @click="excluirAudiencia(audiencia)"
                      >
                        <i class="feather icon-trash-2 mr-2" />
                        Excluir Audiência
                      </b-button>
                      <i
                        v-if="audiencia.status !== 'error'"
                        class="feather icon-trash-2 text-danger cursor-pointer"
                        style="font-size: 1.2rem;"
                        @click="excluirAudiencia(audiencia)"
                      />
                    </div>
                  </div>
                </b-card-body>
              </b-card>
            </b-col>
          </b-row>
        </div>
      </b-card-body>
    </b-card>

    <!-- Modal Escolher Tipo de Audiência -->
    <b-modal
      v-model="modalTipoAudiencia"
      title="Escolha o Tipo de Audiência"
      size="lg"
      hide-footer
      centered
    >
      <p class="text-muted mb-4">
        Selecione como você deseja criar sua audiência:
      </p>

      <b-row>
        <b-col
          cols="12"
          md="6"
          class="mb-3"
        >
          <b-card
            class="cursor-pointer hover-shadow h-100"
            :class="{ 'border-primary': tipoSelecionado === 'segmentada' }"
            @click="selecionarTipoEAvancar('segmentada')"
          >
            <b-card-body class="text-center py-4 d-flex flex-column">
              <div class="mb-3">
                <i
                  class="feather icon-filter"
                  :class="tipoSelecionado === 'segmentada' ? 'text-primary' : 'text-muted'"
                  style="font-size: 3rem;"
                />
              </div>
              <h5 class="mb-2">
                Audiência Segmentada
              </h5>
              <p class="text-muted mb-0 small">
                Crie audiências baseadas em filtros personalizados: idade, gênero, localização, histórico de compras, produtos e muito mais.
              </p>
            </b-card-body>
          </b-card>
        </b-col>

        <b-col
          cols="12"
          md="6"
          class="mb-3"
        >
          <b-card
            class="cursor-pointer hover-shadow h-100"
            :class="{ 'border-primary': tipoSelecionado === 'rfm' }"
            @click="selecionarTipoEAvancar('rfm')"
          >
            <b-card-body class="text-center py-4 d-flex flex-column">
              <div class="mb-3">
                <i
                  class="feather icon-trending-up"
                  :class="tipoSelecionado === 'rfm' ? 'text-primary' : 'text-muted'"
                  style="font-size: 3rem;"
                />
              </div>
              <h5 class="mb-2">
                Audiência RFM
              </h5>
              <p class="text-muted mb-0 small">
                Segmente clientes com base na análise RFM (Recência, Frequência e Valor): Campeões, Leais, Em Risco, Hibernando, etc.
              </p>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
    </b-modal>

    <!-- Modal Criar Nova Audiência -->
    <b-modal
      v-model="modalCriarAudiencia"
      :title="modoVisualizacao ? 'Visualizar Audiência' : 'Criar Nova Audiência'"
      size="lg"
      hide-footer
      :no-close-on-backdrop="salvandoAudiencia"
      :no-close-on-esc="salvandoAudiencia"
      @hidden="resetarFormulario"
    >
      <!-- Overlay de Loading -->
      <div
        v-if="salvandoAudiencia"
        class="modal-loading-overlay"
      >
        <div class="loading-content">
          <b-spinner
            variant="primary"
            style="width: 3rem; height: 3rem;"
            class="mb-3"
          />
          <h5 class="text-white mb-2">
            Salvando Audiência...
          </h5>
          <p class="text-white-50 mb-0">
            Aguarde enquanto processamos sua solicitação
          </p>
        </div>
      </div>

      <b-form
        style="position: relative;"
        @submit.prevent="criarAudiencia"
      >
        <!-- Informações Básicas -->
        <h6 class="mb-3">
          <i class="feather icon-info mr-2" />
          Informações Básicas
        </h6>

        <b-row>
          <b-col md="6">
            <b-form-group
              label="Nome da Audiência"
              label-for="nome-audiencia"
            >
              <b-form-input
                id="nome-audiencia"
                v-model="formulario.name"
                placeholder="Ex: Clientes VIP"
                :disabled="modoVisualizacao"
                required
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group
              label="Descrição"
              label-for="descricao-audiencia"
            >
              <b-form-input
                id="descricao-audiencia"
                v-model="formulario.description"
                placeholder="Descrição opcional"
                :disabled="modoVisualizacao"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <hr class="my-4">

        <!-- Filtros de Segmentação -->
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="mb-0">
            <i class="feather icon-filter mr-2" />
            Filtros de Segmentação
          </h6>
          <b-button
            v-if="!modoVisualizacao"
            variant="outline-primary"
            size="sm"
            :disabled="salvandoAudiencia"
            @click="completarComIA"
          >
            <i class="feather icon-zap mr-2" />
            Completar com IA
          </b-button>
        </div>

        <b-row>
          <!-- Gênero -->
          <b-col md="6">
            <b-form-group label="Gênero">
              <b-form-select
                v-model="formulario.genero"
                :disabled="modoVisualizacao"
              >
                <option value="">
                  Todos
                </option>
                <option value="M">
                  Masculino
                </option>
                <option value="F">
                  Feminino
                </option>
              </b-form-select>
            </b-form-group>
          </b-col>

          <!-- Estados -->
          <b-col md="6">
            <b-form-group>
              <template #label>
                <div class="d-flex justify-content-between align-items-center">
                  <span>Estados</span>
                  <div v-if="!modoVisualizacao">
                    <b-button
                      size="sm"
                      variant="link"
                      class="p-0 mr-2"
                      @click="selecionarTodosEstados"
                    >
                      Selecionar Todos
                    </b-button>
                    <b-button
                      size="sm"
                      variant="link"
                      class="p-0 text-danger"
                      @click="limparEstados"
                    >
                      Limpar
                    </b-button>
                  </div>
                </div>
              </template>
              <!-- Modo edição: v-select normal -->
              <v-select
                v-if="!modoVisualizacao"
                v-model="formulario.estados"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="estadosBrasileiros"
                :reduce="estado => estado.value"
                label="text"
                multiple
                placeholder="Selecione os estados..."
              />
              <!-- Modo visualização: mostrar estados como badges -->
              <div
                v-else
                class="form-control bg-light"
                style="min-height: 38px; height: auto;"
              >
                <template v-if="formulario.estados.length > 0">
                  <b-badge
                    v-for="estado in formulario.estados"
                    :key="estado"
                    variant="primary"
                    class="mr-1 mb-1"
                  >
                    {{ estado }}
                  </b-badge>
                </template>
                <span
                  v-else
                  class="text-muted"
                >
                  Todos os estados
                </span>
              </div>
              <small class="text-muted">
                {{ formulario.estados.length > 0
                  ? `${formulario.estados.length} estado(s) selecionado(s)`
                  : 'Deixe vazio para incluir todos os estados'
                }}
              </small>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <!-- Idade -->
          <b-col md="3">
            <b-form-group label="Idade Mínima">
              <b-form-input
                v-model="formulario.idadeMin"
                type="number"
                min="0"
                max="120"
                placeholder="18"
                :disabled="modoVisualizacao"
              />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Idade Máxima">
              <b-form-input
                v-model="formulario.idadeMax"
                type="number"
                min="0"
                max="120"
                placeholder="65"
                :disabled="modoVisualizacao"
              />
            </b-form-group>
          </b-col>

          <!-- Compras -->
          <b-col md="3">
            <b-form-group label="Mín. Compras">
              <b-form-input
                v-model="formulario.minCompras"
                type="number"
                min="0"
                placeholder="1"
                :disabled="modoVisualizacao"
              />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Máx. Compras">
              <b-form-input
                v-model="formulario.maxCompras"
                type="number"
                min="0"
                placeholder="100"
                :disabled="modoVisualizacao"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <!-- Valor gasto -->
          <b-col md="6">
            <b-form-group label="Valor Mínimo Gasto (R$)">
              <b-form-input
                v-model="formulario.valorMinimo"
                type="number"
                step="0.01"
                min="0"
                placeholder="100.00"
                :disabled="modoVisualizacao"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Valor Máximo Gasto (R$)">
              <b-form-input
                v-model="formulario.valorMaximo"
                type="number"
                step="0.01"
                min="0"
                placeholder="10000.00"
                :disabled="modoVisualizacao"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <!-- Período de compra -->
          <b-col md="6">
            <b-form-group label="Primeira Compra (De)">
              <b-form-input
                v-model="formulario.dataInicioCompras"
                type="date"
                :disabled="modoVisualizacao"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Primeira Compra (Até)">
              <b-form-input
                v-model="formulario.dataFimCompras"
                type="date"
                :disabled="modoVisualizacao"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Tipo de Cliente -->
        <b-row>
          <b-col md="6">
            <b-form-group label="Tipo de Cliente">
              <b-form-select
                v-model="formulario.tipoCliente"
                :disabled="modoVisualizacao"
              >
                <option value="">
                  Todos
                </option>
                <option value="cpf">
                  Pessoa Física (CPF)
                </option>
                <option value="cnpj">
                  Pessoa Jurídica (CNPJ)
                </option>
              </b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Limite de Contatos">
              <b-form-input
                v-model="formulario.limite"
                type="number"
                min="1"
                max="50000"
                placeholder="1000"
                :disabled="modoVisualizacao"
              />
              <small class="text-muted">Máximo 50.000 contatos por audiência</small>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Exclusão de Audiências Existentes -->
        <div
          v-if="!modoVisualizacao && audienciasParaExclusao.length > 0"
          class="mt-4"
        >
          <hr class="my-4">
          <h6 class="mb-2">
            <i class="feather icon-user-minus mr-2" />
            Excluir Leads de Outras Audiências
          </h6>
          <small class="text-muted d-block mb-3">
            Selecione audiências cujos leads NÃO devem aparecer nesta nova audiência.
            Isso evita que o mesmo lead receba mensagens duplicadas.
          </small>

          <b-form-group>
            <v-select
              v-model="audienciasExcluir"
              :options="audienciasParaExclusao"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :reduce="aud => aud.id"
              label="name"
              multiple
              placeholder="Selecione audiências para excluir seus leads..."
              :disabled="salvandoAudiencia"
            >
              <template #option="{ name, lead_count, created_at }">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>{{ name }}</strong>
                    <small
                      v-if="lead_count"
                      class="text-muted d-block"
                    >
                      {{ lead_count }} leads
                    </small>
                  </div>
                  <small class="text-muted">
                    {{ formatarDataAudiencia(created_at) }}
                  </small>
                </div>
              </template>
              <template #selected-option="{ name }">
                <span>{{ name }}</span>
              </template>
              <template #no-options>
                <span class="text-muted">Nenhuma audiência encontrada</span>
              </template>
            </v-select>
            <small
              v-if="audienciasExcluir.length > 0"
              class="text-info mt-1 d-block"
            >
              <i class="feather icon-info mr-1" />
              {{ audienciasExcluir.length }} audiência(s) selecionada(s) para exclusão
            </small>
          </b-form-group>

          <b-button
            v-if="audienciasExcluir.length > 0"
            variant="outline-danger"
            size="sm"
            :disabled="salvandoAudiencia"
            @click="audienciasExcluir = []"
          >
            <i class="feather icon-x mr-1" />
            Limpar Seleção
          </b-button>
        </div>

        <hr class="my-4">

        <!-- Preview dos Resultados da Busca -->
        <div
          v-if="previewContatos.length > 0"
          class="mb-4"
        >
          <h6 class="mb-3">
            <i class="feather icon-users mr-2" />
            Resultados da Busca ({{ previewContatos.length }} contatos encontrados)
          </h6>

          <div class="card">
            <div class="card-body p-0">
              <div class="table-responsive">
                <table class="table table-bordered table-striped mb-0">
                  <thead class="thead-light">
                    <tr>
                      <th
                        class="text-center"
                        style="width: 35%"
                      >
                        Nome
                      </th>
                      <th
                        class="text-center"
                        style="width: 40%"
                      >
                        Email
                      </th>
                      <th
                        class="text-center"
                        style="width: 25%"
                      >
                        Telefone
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(contato, index) in previewContatos.slice(0, 5)"
                      :key="index"
                    >
                      <td class="align-middle">
                        <strong>{{ contato.name || contato.nome || '-' }}</strong>
                      </td>
                      <td class="align-middle">
                        <span class="text-muted">{{ contato.email || '-' }}</span>
                      </td>
                      <td class="align-middle text-center">
                        <code class="text-primary">{{ contato.phone || '-' }}</code>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div class="card-footer bg-light">
              <div class="d-flex justify-content-between align-items-center">
                <small class="text-muted">
                  <i class="feather icon-info mr-1" />
                  Mostrando apenas os primeiros 5 resultados
                </small>
                <small class="text-success font-weight-medium">
                  Total: {{ previewContatos.length }} contatos
                </small>
              </div>
            </div>
          </div>
        </div>

        <!-- Botões -->
        <div v-if="!modoVisualizacao">
          <!-- Aviso se não fez preview -->
          <div
            v-if="!previewRealizado"
            class="alert alert-warning d-flex align-items-center mb-3"
          >
            <i class="feather icon-alert-triangle mr-2" />
            <span>
              <strong>Atenção:</strong> Clique em "Buscar Audiências" para visualizar os contatos antes de salvar.
            </span>
          </div>

          <div class="d-flex justify-content-between">
            <div>
              <b-button
                variant="outline-secondary"
                :disabled="salvandoAudiencia"
                @click="testarFiltros"
              >
                <i class="feather icon-search mr-2" />
                Buscar Audiências
              </b-button>
            </div>
            <div>
              <b-button
                variant="secondary"
                class="mr-2"
                :disabled="salvandoAudiencia"
                @click="modalCriarAudiencia = false"
              >
                Cancelar
              </b-button>
              <b-button
                type="submit"
                variant="primary"
                :disabled="salvandoAudiencia || !formulario.name || !previewRealizado"
                :title="!previewRealizado ? 'Clique em Buscar Audiências primeiro' : ''"
              >
                <b-spinner
                  v-if="salvandoAudiencia"
                  small
                  class="mr-2"
                />
                <i
                  v-else
                  class="feather icon-save mr-2"
                />
                {{ salvandoAudiencia ? 'Salvando...' : 'Salvar Audiência' }}
              </b-button>
            </div>
          </div>
        </div>

        <!-- Botão para modo visualização -->
        <div
          v-else
          class="d-flex justify-content-between"
        >
          <b-button
            variant="success"
            :disabled="previewContatos.length === 0"
            @click="baixarCSV"
          >
            <i class="feather icon-download mr-2" />
            Baixar CSV
          </b-button>

          <b-button
            variant="secondary"
            @click="modalCriarAudiencia = false"
          >
            <i class="feather icon-x mr-2" />
            Fechar
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <!-- Modal Completar com IA -->
    <b-modal
      v-model="modalIA"
      title="🤖 Completar Filtros com IA"
      size="md"
      hide-footer
      @hidden="mensagemIA = ''"
    >
      <div class="mb-3">
        <p class="text-muted mb-3">
          Descreva em linguagem natural quais clientes você quer filtrar e a IA irá configurar os filtros automaticamente.
        </p>

        <b-form-group>
          <template #label>
            <div class="d-flex justify-content-between align-items-center">
              <span>Sua solicitação:</span>
              <small :class="mensagemIA.length > 1000 ? 'text-danger' : 'text-muted'">
                {{ mensagemIA.length }}/1000 caracteres
              </small>
            </div>
          </template>

          <b-form-textarea
            v-model="mensagemIA"
            placeholder="Ex: Todos os clientes masculinos de São Paulo que compraram nos últimos 30 dias"
            rows="5"
            maxlength="1000"
            :state="mensagemIA.length > 1000 ? false : null"
          />
        </b-form-group>

        <b-form-group label="Limite de Contatos">
          <b-form-input
            v-model.number="formulario.limite"
            type="number"
            min="1"
            max="50000"
            placeholder="1000"
          />
          <small class="text-muted">Máximo 50.000 contatos por audiência</small>
        </b-form-group>

        <div class="alert alert-info">
          <strong>💡 Exemplos:</strong>
          <ul class="mb-0 mt-2">
            <li>"Todos os clientes masculinos"</li>
            <li>"Mulheres de 25 a 35 anos que compraram mais de 3 vezes"</li>
            <li>"Clientes do Rio de Janeiro e São Paulo"</li>
            <li>"Quem gastou mais de R$ 500 nos últimos 60 dias"</li>
          </ul>
        </div>
      </div>

      <div class="d-flex justify-content-end gap-2">
        <b-button
          variant="secondary"
          :disabled="processandoIA"
          @click="modalIA = false"
        >
          Cancelar
        </b-button>
        <b-button
          variant="primary"
          :disabled="processandoIA || !mensagemIA.trim() || mensagemIA.length > 1000"
          @click="processarComIA"
        >
          <b-spinner
            v-if="processandoIA"
            small
            class="mr-2"
          />
          <i
            v-else
            class="feather icon-zap mr-2"
          />
          {{ processandoIA ? 'Processando...' : 'Processar com IA' }}
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { getUserData } from '@/auth/utils'
import vSelect from 'vue-select'
import {
  getAudiences,
  createAudience,
  getAudience,
  previewAudience,
  chatAiForSearchAudience,
  deleteAudience,
} from '../../functions/basics/campaigns'
import stores from '../../functions/basics/stores'

export default {
  name: 'AudienciaPage',
  components: {
    vSelect,
  },
  data() {
    return {
      // Estado da página
      loading: false,
      filtroNome: '',

      // Lojas
      stores: [],
      storeUuid: '',

      // Audiências
      audiencias: [],

      // Estatísticas
      estatisticas: {
        totalAudiencias: 0,
        totalContatos: 0,
        audienciasAtivas: 0,
        criadasHoje: 0,
      },

      // Modal escolher tipo de audiência
      modalTipoAudiencia: false,
      tipoSelecionado: null,

      // Modal criar audiência
      modalCriarAudiencia: false,
      salvandoAudiencia: false,
      modoVisualizacao: false,
      audienciaAtual: null, // Para armazenar a audiência sendo visualizada

      // Formulário de criação
      formulario: {
        name: '',
        description: '',
        genero: '',
        estados: [],
        idadeMin: '',
        idadeMax: '',
        minCompras: '',
        maxCompras: '',
        valorMinimo: '',
        valorMaximo: '',
        dataInicioCompras: '',
        dataFimCompras: '',
        tipoCliente: '',
        limite: 1000,
      },

      // Lista de estados brasileiros
      estadosBrasileiros: [
        { value: 'AC', text: 'Acre (AC)' },
        { value: 'AL', text: 'Alagoas (AL)' },
        { value: 'AP', text: 'Amapá (AP)' },
        { value: 'AM', text: 'Amazonas (AM)' },
        { value: 'BA', text: 'Bahia (BA)' },
        { value: 'CE', text: 'Ceará (CE)' },
        { value: 'DF', text: 'Distrito Federal (DF)' },
        { value: 'ES', text: 'Espírito Santo (ES)' },
        { value: 'GO', text: 'Goiás (GO)' },
        { value: 'MA', text: 'Maranhão (MA)' },
        { value: 'MT', text: 'Mato Grosso (MT)' },
        { value: 'MS', text: 'Mato Grosso do Sul (MS)' },
        { value: 'MG', text: 'Minas Gerais (MG)' },
        { value: 'PA', text: 'Pará (PA)' },
        { value: 'PB', text: 'Paraíba (PB)' },
        { value: 'PR', text: 'Paraná (PR)' },
        { value: 'PE', text: 'Pernambuco (PE)' },
        { value: 'PI', text: 'Piauí (PI)' },
        { value: 'RJ', text: 'Rio de Janeiro (RJ)' },
        { value: 'RN', text: 'Rio Grande do Norte (RN)' },
        { value: 'RS', text: 'Rio Grande do Sul (RS)' },
        { value: 'RO', text: 'Rondônia (RO)' },
        { value: 'RR', text: 'Roraima (RR)' },
        { value: 'SC', text: 'Santa Catarina (SC)' },
        { value: 'SP', text: 'São Paulo (SP)' },
        { value: 'SE', text: 'Sergipe (SE)' },
        { value: 'TO', text: 'Tocantins (TO)' },
      ],

      // Preview
      previewContatos: [],
      previewRealizado: false,

      // IA Modal
      modalIA: false,
      mensagemIA: '',
      processandoIA: false,

      // Exclusão de audiências existentes
      audienciasExcluir: [],
    }
  },

  computed: {
    audienciasFiltradas() {
      if (!this.filtroNome) {
        return this.audiencias
      }

      return this.audiencias.filter(audiencia => audiencia.name.toLowerCase().includes(this.filtroNome.toLowerCase())
        || (audiencia.description && audiencia.description.toLowerCase().includes(this.filtroNome.toLowerCase())))
    },

    storeOptions() {
      return this.stores.map(store => ({
        value: store.uuid,
        text: store.name || `Loja ${store.uuid.substring(0, 8)}`,
      }))
    },

    audienciasParaExclusao() {
      // Retornar audiências disponíveis para seleção de exclusão
      // Excluir a audiência atual se estiver em modo visualização
      return this.audiencias
        .filter(aud => !this.audienciaAtual || aud.id !== this.audienciaAtual.id)
        .map(aud => ({
          id: aud.id,
          name: aud.name || 'Audiência sem nome',
          lead_count: aud.lead_count || 0,
          created_at: aud.created_at,
        }))
    },
  },

  watch: {
    'formulario.limite': function (newVal) {
      // Validar e ajustar o limite se exceder 50000
      if (newVal && Number(newVal) > 50000) {
        this.$nextTick(() => {
          this.formulario.limite = 50000
          this.$bvToast.toast('O limite máximo é de 50.000 contatos. Valor ajustado automaticamente.', {
            title: 'Atenção',
            variant: 'warning',
            solid: true,
            autoHideDelay: 3000,
          })
        })
      }
    },
  },

  async mounted() {
    try {
      // Carregar lojas
      this.stores = await stores()

      // Lógica de seleção de loja
      if (this.stores.length === 1) {
        // Se há apenas 1 loja, usar automaticamente
        this.storeUuid = this.stores[0].uuid
        console.log('🏪 Loja única selecionada automaticamente:', this.stores[0].name || this.storeUuid)

        // Carregar dados automaticamente
        await this.carregarAudiencias()
      } else if (this.stores.length > 1) {
        // Se há múltiplas lojas, selecionar a primeira por padrão
        this.storeUuid = this.stores[0].uuid
        console.log('🏪 Primeira loja selecionada por padrão:', this.stores[0].name || this.storeUuid)

        // Carregar dados automaticamente
        await this.carregarAudiencias()
      } else {
        console.warn('⚠️ Nenhuma loja encontrada')
      }
    } catch (error) {
      console.error('❌ Erro ao carregar lojas:', error)
      this.$bvToast.toast('Erro ao carregar lojas', {
        title: 'Erro',
        variant: 'danger',
        solid: true,
      })
    }
  },

  methods: {
    async onStoreChange() {
      if (this.storeUuid) {
        console.log('🏪 Loja selecionada:', this.storeUuid)
        await this.carregarAudiencias()
      }
    },

    selecionarTodosEstados() {
      this.formulario.estados = this.estadosBrasileiros.map(e => e.value)
    },

    limparEstados() {
      this.formulario.estados = []
    },

    async carregarAudiencias() {
      this.loading = true

      try {
        const audiences = await getAudiences(this.storeUuid)
        this.audiencias = audiences || []
        this.estatisticas = this.calcularEstatisticas()
      } catch (error) {
        console.error('Erro ao carregar audiências:', error)
        this.$bvToast.toast('Erro ao carregar audiências', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.loading = false
      }
    },

    abrirModalTipoAudiencia() {
      this.tipoSelecionado = null
      this.modalTipoAudiencia = true
    },

    selecionarTipoEAvancar(tipo) {
      this.tipoSelecionado = tipo
      this.modalTipoAudiencia = false

      if (tipo === 'segmentada') {
        // Redirecionar para a página de segmentação tradicional
        this.$router.push({
          name: 'audiencia-segmentacao',
          params: { uuid: this.storeUuid },
        })
      } else if (tipo === 'rfm') {
        // Redirecionar para a página de audiência RFM
        this.$router.push({
          name: 'audiencia-rfm',
          params: { uuid: this.storeUuid },
        })
      }
    },

    irParaSegmentacao() {
      // Redirecionar para a página de segmentação
      this.$router.push({
        name: 'audiencia-segmentacao',
        params: { uuid: this.storeUuid },
      })
    },

    completarComIA() {
      // Abrir modal de IA
      this.modalIA = true
      this.mensagemIA = ''
    },

    async processarComIA() {
      if (!this.mensagemIA.trim()) {
        this.$bvToast.toast('Digite uma descrição para a IA processar', {
          title: 'Atenção',
          variant: 'warning',
          solid: true,
          autoHideDelay: 3000,
        })
        return
      }

      if (this.mensagemIA.length > 1000) {
        this.$bvToast.toast('A mensagem não pode ter mais de 1000 caracteres', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
          autoHideDelay: 3000,
        })
        return
      }

      // Validar limite antes de enviar para IA
      const limitForm = Number(this.formulario.limite)
      if (Number.isFinite(limitForm) && limitForm > 50000) {
        this.$bvToast.toast('O limite máximo é de 50.000 contatos', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
          autoHideDelay: 3000,
        })
        return
      }

      this.processandoIA = true

      try {
        console.log('🤖 Enviando para IA:', this.mensagemIA)
        console.log('🏪 Store UUID:', this.storeUuid)

        // Garantir que o limite não exceda 50000
        const limite = Math.min(parseInt(this.formulario.limite) || 5, 50000)
        const response = await chatAiForSearchAudience(
          this.mensagemIA,
          this.storeUuid,
          limite,
        )

        console.log('✅ Resposta da IA:', response)

        if (response && response.data) {
          // Verificar se tem filters ou preview
          if (response.data.filters.structured) {
            // Preencher os filtros com os dados retornados
            this.preencherFiltrosIA(response.data.filters.structured)
          } else if (response.data.preview) {
            // Extrair filtros do preview.sample
            this.extrairFiltrosDoSample(response.data.preview)
          }

          // Se tem preview, mostrar os contatos
          if (response.data.preview && response.data.preview.sample) {
            this.previewContatos = response.data.preview.sample
            console.log('👥 Preview de contatos:', this.previewContatos.length)
          }

          // Fechar o modal
          this.modalIA = false
          this.mensagemIA = ''

          const totalCount = (response.data && response.data.preview && response.data.preview.total_count) ? response.data.preview.total_count : 0
          this.$bvToast.toast(`Filtros aplicados! ${totalCount} contatos encontrados.`, {
            title: 'Sucesso',
            variant: 'success',
            solid: true,
            autoHideDelay: 3000,
          })
        }
      } catch (error) {
        console.error('❌ Erro ao processar com IA:', error)
        this.$bvToast.toast('Erro ao processar sua solicitação. Tente novamente.', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000,
        })
      } finally {
        this.processandoIA = false
      }
    },

    preencherFiltrosIA(filters) {
      // Preencher os campos do formulário com os filtros da IA

      // Gênero
      if (filters.genero) {
        this.formulario.genero = filters.genero
      }

      // Estados
      if (filters.estados) {
        this.formulario.estados = Array.isArray(filters.estados)
          ? filters.estados
          : [filters.estados]
      }

      // Idade
      if (filters.idadeMin !== undefined) {
        this.formulario.idadeMin = filters.idadeMin
      }
      if (filters.idadeMax !== undefined) {
        this.formulario.idadeMax = filters.idadeMax
      }

      // Compras
      if (filters.minCompras !== undefined) {
        this.formulario.minCompras = filters.minCompras
      }
      if (filters.maxCompras !== undefined) {
        this.formulario.maxCompras = filters.maxCompras
      }

      // Valor
      if (filters.valorMinimo !== undefined) {
        this.formulario.valorMinimo = filters.valorMinimo
      }
      if (filters.valorMaximo !== undefined) {
        this.formulario.valorMaximo = filters.valorMaximo
      }

      // Datas
      if (filters.dataInicioCompras) {
        this.formulario.dataInicioCompras = filters.dataInicioCompras
      }
      if (filters.dataFimCompras) {
        this.formulario.dataFimCompras = filters.dataFimCompras
      }

      // Tipo de cliente
      if (filters.tipo_cliente) {
        this.formulario.tipoCliente = filters.tipo_cliente
      }

      console.log('📝 Formulário preenchido:', this.formulario)
    },

    extrairFiltrosDoSample(preview) {
      // Extrair filtros analisando o sample de contatos
      if (!preview.sample || preview.sample.length === 0) {
        console.warn('⚠️ Nenhum sample para extrair filtros')
        return
      }

      const { sample } = preview

      // Extrair gênero (se todos forem do mesmo gênero)
      const generos = [...new Set(sample.map(c => c.genero).filter(Boolean))]
      if (generos.length === 1) {
        this.formulario.genero = generos[0]
      }

      // Extrair estados únicos
      const estados = [...new Set(sample.map(c => c.estado).filter(Boolean))]
      if (estados.length > 0) {
        this.formulario.estados = estados
      }

      // Extrair faixa de idade
      const idades = sample.map(c => c.idade).filter(i => i !== null && i !== undefined)
      if (idades.length > 0) {
        this.formulario.idadeMin = Math.min(...idades)
        this.formulario.idadeMax = Math.max(...idades)
      }

      // Extrair faixa de pedidos
      const totalPedidos = sample.map(c => c.total_pedidos).filter(p => p !== null && p !== undefined)
      if (totalPedidos.length > 0) {
        this.formulario.minCompras = Math.min(...totalPedidos)
        this.formulario.maxCompras = Math.max(...totalPedidos)
      }

      // Extrair faixa de valor
      const valores = sample.map(c => c.valor_total).filter(v => v !== null && v !== undefined)
      if (valores.length > 0) {
        this.formulario.valorMinimo = Math.min(...valores)
        this.formulario.valorMaximo = Math.max(...valores)
      }

      // Extrair data do último pedido (mais recente e mais antigo do sample)
      const datas = sample.map(c => c.ultimo_pedido).filter(Boolean)
      if (datas.length > 0) {
        const datasOrdenadas = datas.sort()
        this.formulario.dataInicioCompras = datasOrdenadas[0].split('T')[0]
        this.formulario.dataFimCompras = datasOrdenadas[datasOrdenadas.length - 1].split('T')[0]
      }

      console.log('📝 Filtros extraídos do sample:', {
        genero: this.formulario.genero,
        estados: this.formulario.estados,
        idade: `${this.formulario.idadeMin} - ${this.formulario.idadeMax}`,
        compras: `${this.formulario.minCompras} - ${this.formulario.maxCompras}`,
        valor: `${this.formulario.valorMinimo} - ${this.formulario.valorMaximo}`,
        datas: `${this.formulario.dataInicioCompras} - ${this.formulario.dataFimCompras}`,
      })
    },

    /**
     * Monta o payload { filters, limit } para o previewAudience
     */
    montarParametrosPreview(limitPadrao = 5) {
      const filtros = {}

      // Gênero
      if (this.formulario.genero) filtros.genero = this.formulario.genero

      // Estados -> array
      if (this.formulario.estados && this.formulario.estados.length > 0) {
        filtros.estado = this.formulario.estados
      }

      // Idade - só envia se tiver valor válido e maior que 0
      const idadeMin = Number(this.formulario.idadeMin)
      const idadeMax = Number(this.formulario.idadeMax)
      if (Number.isFinite(idadeMin) && idadeMin > 0) filtros.idadeMin = idadeMin
      if (Number.isFinite(idadeMax) && idadeMax > 0) filtros.idadeMax = idadeMax

      // Compras - só envia se tiver valor válido e maior que 0
      const minCompras = Number(this.formulario.minCompras)
      const maxCompras = Number(this.formulario.maxCompras)
      if (Number.isFinite(minCompras) && minCompras > 0) filtros.minCompras = minCompras
      if (Number.isFinite(maxCompras) && maxCompras > 0) filtros.maxCompras = maxCompras

      // Valor gasto - só envia se tiver valor válido e maior que 0
      const valorMinimo = Number(this.formulario.valorMinimo)
      const valorMaximo = Number(this.formulario.valorMaximo)
      if (Number.isFinite(valorMinimo) && valorMinimo > 0) filtros.valorMinimo = valorMinimo
      if (Number.isFinite(valorMaximo) && valorMaximo > 0) filtros.valorMaximo = valorMaximo

      // Período de compra (strings yyyy-mm-dd)
      if (this.formulario.dataInicioCompras) filtros.dataInicioCompras = this.formulario.dataInicioCompras
      if (this.formulario.dataFimCompras) filtros.dataFimCompras = this.formulario.dataFimCompras

      // Tipo de cliente
      if (this.formulario.tipoCliente) filtros.tipoCliente = this.formulario.tipoCliente

      // Audiências a excluir (leads que já pertencem a essas audiências)
      if (this.audienciasExcluir && this.audienciasExcluir.length > 0) {
        filtros.excluir_audiencias = this.audienciasExcluir
        console.log('🚫 Audiências para exclusão no preview:', this.audienciasExcluir)
      }

      // Limite - usa valor do formulário se for válido e > 0, senão usa padrão
      // Garantir que o limite não exceda 50000
      const limitForm = Number(this.formulario.limite)
      const limitRaw = Number.isFinite(limitForm) && limitForm > 0 ? limitForm : limitPadrao
      const limit = Math.min(limitRaw, 50000)

      console.log('🔧 Filtros montados:', filtros)
      return { filters: filtros, limit }
    },

    /**
     * Preview usando APENAS previewAudience(storeUuid, { filters, limit })
     */
    async testarFiltros() {
      try {
        // Validar limite antes de enviar
        const limitForm = Number(this.formulario.limite)
        if (Number.isFinite(limitForm) && limitForm > 50000) {
          this.$bvToast.toast('O limite máximo é de 50.000 contatos', {
            title: 'Erro',
            variant: 'danger',
            solid: true,
            autoHideDelay: 3000,
          })
          return
        }

        // Limita a 5 resultados para não ocupar muito espaço
        const params = this.montarParametrosPreview(5)
        console.log('🔍 Enviando previewAudience com params:', params)

        const response = await previewAudience(this.storeUuid, params)

        // 🔍 DEBUG: Logs temporários para identificar a estrutura
        console.log('🔍 Response completa:', response)
        console.log('🔍 Response type:', typeof response)
        console.log('🔍 Response keys:', Object.keys(response || {}))

        // Normalização - response deveria ser { results: [...], count: X }
        const payload = response
        const metaTop = response

        console.log('🔍 Payload:', payload)
        console.log('🔍 Payload.results:', (payload && payload.results) ? payload.results : undefined)
        console.log('🔍 Payload.results type:', (payload && payload.results) ? typeof payload.results : undefined)
        console.log('🔍 Payload.results length:', (payload && payload.results && payload.results.length) ? payload.results.length : undefined)
        console.log('🔍 Payload.count:', (payload && payload.count) ? payload.count : undefined)

        // Extrair results com mais verificações
        let results = []
        if (payload && payload.results && Array.isArray(payload.results)) {
          results = payload.results
          console.log('✅ Results extraídos com sucesso:', results.length, 'contatos')
        } else {
          console.log('❌ Payload.results não é um array válido')
          console.log('🔍 Tentando payload.data.results:', (payload && payload.data && payload.data.results) ? payload.data.results : undefined)
          if (payload && payload.data && payload.data.results && Array.isArray(payload.data.results)) {
            results = payload.data.results
            console.log('✅ Results extraídos de payload.data.results:', results.length, 'contatos')
          }
        }

        console.log('🔍 Results final:', results)
        console.log('🔍 Results final length:', results.length)

        // Normalizar para a tabela (nome, phone, email, estado, genero)
        this.previewContatos = results.map(r => {
          console.log('🔍 Processando contato:', r)
          return {
            nome: r.name || r.nome || r.display_name || '-',
            phone: r.phone || r.telefone || r.msisdn || '-',
            email: r.email || r.mail || '-',
            estado: r.estado || r.state || r.uf || (r.extra_data && (r.extra_data.estado || r.extra_data.state || r.extra_data.uf)) || '',
            genero: r.genero || r.gender || (r.extra_data && (r.extra_data.genero || r.extra_data.gender)) || '',
          }
        })

        console.log('🔍 previewContatos final:', this.previewContatos)

        const total = (payload && payload.count !== undefined) ? payload.count : results.length
        const limitedTo = (payload && payload.limited_to !== undefined) ? payload.limited_to : params.limit

        // Marcar que o preview foi realizado
        this.previewRealizado = true

        this.$bvToast.toast(
          `Foram encontrados ${this.previewContatos.length} contatos`,
          {
            title: 'Preview da Audiência',
            variant: 'success',
            solid: true,
          },
        )

        // Logs úteis
        console.log('📊 Preview Audience payload:', payload)
        if (metaTop && metaTop.message) console.log('ℹ️ Mensagem da API:', metaTop.message)
        if (payload && payload.query) console.log('🔎 Query executada:', payload.query)
        if (payload && payload.filters_applied) console.log('🧩 Filtros aplicados:', payload.filters_applied)
      } catch (error) {
        console.error('❌ Erro no previewAudience:', error)
        console.error('❌ Response data:', (error && error.response && error.response.data) ? error.response.data : undefined)

        const data = (error && error.response && error.response.data) ? error.response.data : null
        const errorMessage = (data && data.error_message)
          || (data && data.error)
          || (data && data.message)
          || (error && error.message)
          || 'Erro no preview de segmentação'

        this.$bvToast.toast(errorMessage, {
          title: 'Erro no Preview',
          variant: 'danger',
          solid: true,
          autoHideDelay: 8000,
        })
      }
    },

    /**
     * Carrega leads já salvos de uma audiência (não faz preview - dados já processados)
     * Usado ao visualizar audiências existentes
     */
    async carregarLeadsSalvos(response) {
      try {
        console.log('📥 Carregando leads salvos da audiência:', response)

        // O endpoint /leads retorna: { audience, leads, count, from_saved_leads }
        const leads = response.leads || []
        const count = response.count || 0

        // Atualizar o preview com os leads salvos
        this.previewContatos = leads.map(lead => ({
          id: lead.id,
          lead_store_id: lead.lead_store_id,
          // Tenta diferentes campos possíveis para o nome
          name: lead.name || lead.nome || lead.customer_name || lead.lead_name || 'Nome não disponível',
          phone: lead.phone || lead.telefone || lead.customer_phone || 'Telefone não disponível',
          email: lead.email || lead.customer_email || 'Email não disponível',
          is_opted_out: lead.is_opted_out || 0,
        }))

        this.totalContatos = count
        this.previewRealizado = true

        console.log('📊 Preview atualizado com leads salvos:', this.previewContatos.length, 'contatos')

        this.$bvToast.toast(`${count} lead(s) carregado(s) da audiência`, {
          title: 'Leads Carregados',
          variant: 'success',
          solid: true,
        })
      } catch (error) {
        console.error('❌ Erro ao carregar leads salvos:', error)

        this.$bvToast.toast('Erro ao carregar leads da audiência', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      }
    },

    async criarAudiencia() {
      // Validação: precisa ter feito preview antes
      if (!this.previewRealizado) {
        this.$bvToast.toast('Clique em "Buscar Audiências" para visualizar os contatos antes de salvar.', {
          title: 'Atenção',
          variant: 'warning',
          solid: true,
          autoHideDelay: 4000,
        })
        return
      }

      // Validar limite antes de criar
      const limitForm = Number(this.formulario.limite)
      if (Number.isFinite(limitForm) && limitForm > 50000) {
        this.$bvToast.toast('O limite máximo é de 50.000 contatos', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
          autoHideDelay: 3000,
        })
        return
      }

      this.salvandoAudiencia = true
      console.log('🔍 Criando audiência:', this.formulario)
      try {
        // Preparar dados da audiência, removendo campos vazios
        const audienceData = {
          name: this.formulario.name,
          description: this.formulario.description || null,
          source_kql: this.gerarKQL(),
          filters: {},
        }

        // Adicionar filtros apenas se preenchidos
        if (this.formulario.genero) {
          audienceData.filters.genero = this.formulario.genero
        }

        if (this.formulario.estados && this.formulario.estados.length > 0) {
          audienceData.filters.estados = this.formulario.estados
        }

        if (this.formulario.idadeMin) {
          audienceData.filters.idadeMin = parseInt(this.formulario.idadeMin)
        }

        if (this.formulario.idadeMax) {
          audienceData.filters.idadeMax = parseInt(this.formulario.idadeMax)
        }

        if (this.formulario.minCompras) {
          audienceData.filters.minCompras = parseInt(this.formulario.minCompras)
        }

        if (this.formulario.maxCompras) {
          audienceData.filters.maxCompras = parseInt(this.formulario.maxCompras)
        }

        if (this.formulario.valorMinimo) {
          audienceData.filters.valorMinimo = parseFloat(this.formulario.valorMinimo)
        }

        if (this.formulario.valorMaximo) {
          audienceData.filters.valorMaximo = parseFloat(this.formulario.valorMaximo)
        }

        if (this.formulario.dataInicioCompras) {
          audienceData.filters.dataInicioCompras = this.formulario.dataInicioCompras
        }

        if (this.formulario.dataFimCompras) {
          audienceData.filters.dataFimCompras = this.formulario.dataFimCompras
        }

        if (this.formulario.tipoCliente) {
          audienceData.filters.tipoCliente = this.formulario.tipoCliente
        }

        if (this.formulario.limite) {
          // Garantir que o limite não exceda 50000
          const limite = Math.min(parseInt(this.formulario.limite), 50000)
          audienceData.filters.limite = limite
        }

        // Audiências a excluir (leads que já pertencem a essas audiências)
        if (this.audienciasExcluir && this.audienciasExcluir.length > 0) {
          audienceData.filters.excluir_audiencias = this.audienciasExcluir
          console.log('🚫 Audiências para exclusão:', this.audienciasExcluir)
        }

        const response = await createAudience(this.storeUuid, audienceData)
        console.log('✅ Audiência criada:', response)

        // Recarregar a lista de audiências
        await this.carregarAudiencias()

        // Fechar modal
        this.modalCriarAudiencia = false

        this.$bvToast.toast('Audiência criada com sucesso!', {
          title: 'Sucesso',
          variant: 'success',
          solid: true,
        })
      } catch (error) {
        console.error('Erro ao criar audiência:', error)

        // Mostrar erro mais específico se disponível
        const errorData = (error && error.response && error.response.data) ? error.response.data : null
        const errorMessage = (errorData && errorData.error_message)
          || (errorData && errorData.error)
          || (errorData && errorData.message)
          || 'Erro ao criar audiência'

        this.$bvToast.toast(errorMessage, {
          title: 'Erro',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000,
        })
      } finally {
        this.salvandoAudiencia = false
      }
    },

    async visualizarAudiencia(audiencia) {
      try {
        console.log('📥 Visualizando audiência:', audiencia)

        // Resetar formulário antes de preencher
        this.formulario = {
          name: '',
          description: '',
          genero: '',
          estados: [],
          idadeMin: '',
          idadeMax: '',
          minCompras: '',
          maxCompras: '',
          valorMinimo: '',
          valorMaximo: '',
          dataInicioCompras: '',
          dataFimCompras: '',
          tipoCliente: '',
          limite: 1000,
        }

        // O source_kql já vem no objeto audiencia da listagem
        const sourceKql = audiencia.source_kql || ''

        console.log('🔍 DEBUG - Audiência ID:', audiencia.id)
        console.log('🔍 DEBUG - Source KQL:', sourceKql)

        // Armazenar a audiência atual para usar no preview
        this.audienciaAtual = audiencia

        // Preencher informações básicas
        this.formulario.name = audiencia.name || ''
        this.formulario.description = audiencia.description || ''

        // Extrair filtros do source_kql
        if (sourceKql) {
          this.parseKQLParaFormulario(sourceKql)
        }

        console.log('📝 Formulário preenchido:', this.formulario)
        console.log('🗺️ Estados no formulário:', this.formulario.estados)

        // Abrir o modal no modo visualização
        this.modoVisualizacao = true
        this.modalCriarAudiencia = true

        // Aguardar o próximo tick para garantir que o Vue atualizou o DOM
        await this.$nextTick()

        // Buscar os leads da audiência
        const leadsResponse = await getAudience(this.storeUuid, audiencia.id)
        await this.carregarLeadsSalvos(leadsResponse)

        this.$bvToast.toast(`Visualizando: ${audiencia.name}`, {
          title: 'Audiência Carregada',
          variant: 'info',
          solid: true,
        })
      } catch (error) {
        console.error('❌ Erro ao visualizar audiência:', error)
        this.$bvToast.toast('Erro ao carregar audiência', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      }
    },

    async excluirAudiencia(audiencia) {
      try {
        // Confirmar exclusão
        const confirmacao = await this.$bvModal.msgBoxConfirm(
          `Tem certeza que deseja excluir a audiência "${audiencia.name}"? Esta ação não pode ser desfeita.`,
          {
            title: 'Confirmar Exclusão',
            size: 'md',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'Sim, Excluir',
            cancelTitle: 'Cancelar',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true,
          },
        )

        if (!confirmacao) {
          return
        }

        // Excluir audiência
        await deleteAudience(this.storeUuid, audiencia.id)

        // Mostrar mensagem de sucesso
        this.$bvToast.toast('Audiência excluída com sucesso!', {
          title: 'Sucesso',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000,
        })

        // Recarregar lista de audiências
        await this.carregarAudiencias()
      } catch (error) {
        console.error('❌ Erro ao excluir audiência:', error)

        const errorData = (error && error.response && error.response.data) ? error.response.data : null
        const errorMessage = (errorData && errorData.error_message)
          || (errorData && errorData.error)
          || (errorData && errorData.message)
          || 'Erro ao excluir audiência'

        this.$bvToast.toast(errorMessage, {
          title: 'Erro',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000,
        })
      }
    },

    resetarFormulario() {
      this.formulario = {
        name: '',
        description: '',
        genero: '',
        estados: [],
        idadeMin: '',
        idadeMax: '',
        minCompras: '',
        maxCompras: '',
        valorMinimo: '',
        valorMaximo: '',
        dataInicioCompras: '',
        dataFimCompras: '',
        tipoCliente: '',
        limite: 1000,
      }
      this.previewContatos = []
      this.previewRealizado = false
      this.modoVisualizacao = false
      this.audienciaAtual = null
      this.audienciasExcluir = []
    },

    formatarData(data) {
      if (!data) return '-'
      return new Date(data).toLocaleDateString('pt-BR')
    },

    formatarDataAudiencia(data) {
      if (!data) return ''
      try {
        const date = new Date(data)
        return date.toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
      } catch {
        return ''
      }
    },

    baixarCSV() {
      if (this.previewContatos.length === 0) {
        this.$bvToast.toast('Nenhum contato disponível para download', {
          title: 'Atenção',
          variant: 'warning',
          solid: true,
          autoHideDelay: 3000,
        })
        return
      }

      try {
        // Criar cabeçalho do CSV conforme solicitado: Nome, Email, Telefone
        const headers = ['Nome', 'Email', 'Telefone']

        // Criar linhas do CSV
        const rows = this.previewContatos.map(contato => [
          contato.nome || '',
          contato.email || '',
          contato.phone || '',
        ])

        // Combinar cabeçalho e linhas
        const csvContent = [
          headers.join(','),
          ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
        ].join('\n')

        // Criar blob e fazer download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)

        // Nome do arquivo com data e nome da audiência
        const nomeAudiencia = this.formulario.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()
        const dataAtual = new Date().toISOString().split('T')[0]
        const nomeArquivo = `audiencia_${nomeAudiencia}_${dataAtual}.csv`

        link.setAttribute('href', url)
        link.setAttribute('download', nomeArquivo)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        this.$bvToast.toast(`CSV baixado com sucesso! ${this.previewContatos.length} contatos exportados.`, {
          title: 'Sucesso',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000,
        })

        console.log('📥 CSV gerado:', {
          arquivo: nomeArquivo,
          totalContatos: this.previewContatos.length,
        })
      } catch (error) {
        console.error('❌ Erro ao gerar CSV:', error)
        this.$bvToast.toast('Erro ao gerar arquivo CSV', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000,
        })
      }
    },

    calcularEstatisticas() {
      // Total de contatos apenas das audiências ativas
      const totalContatos = this.audiencias
        .filter(aud => aud.is_active)
        .reduce((total, aud) => total + (aud.total_contacts || aud.contact_count || 0), 0)

      // Audiências criadas hoje
      const hoje = new Date()
      const hojeStr = hoje.toISOString().split('T')[0] // YYYY-MM-DD
      const criadasHoje = this.audiencias.filter(aud => {
        if (!aud.created_at) return false
        const createdAt = new Date(aud.created_at)
        const createdStr = createdAt.toISOString().split('T')[0]
        return createdStr === hojeStr
      }).length

      return {
        totalAudiencias: this.audiencias.length,
        totalContatos,
        audienciasAtivas: this.audiencias.filter(aud => aud.is_active).length,
        criadasHoje,
      }
    },

    gerarKQL() {
      // Mantido para persistência do source_kql ao salvar a audiência
      let kql = 'clientes'
      const filtros = []

      // 🔒 Filtro de segurança
      filtros.push(`store_uuid == "${this.storeUuid}"`)
      // Filtro básico: ter telefone
      filtros.push('isnotempty(phone)')

      // Gênero
      if (this.formulario.genero) {
        filtros.push(`genero == "${this.formulario.genero}"`)
      }

      // Estados
      if (this.formulario.estados && this.formulario.estados.length > 0) {
        const { estados } = this.formulario
        if (estados.length === 1) {
          filtros.push(`estado == "${estados[0]}"`)
        } else if (estados.length > 1) {
          const estadosStr = estados.map(e => `"${e}"`).join(', ')
          filtros.push(`estado in (${estadosStr})`)
        }
      }

      // Idade
      if (this.formulario.idadeMin && this.formulario.idadeMax) {
        filtros.push(`idade between (${this.formulario.idadeMin} .. ${this.formulario.idadeMax})`)
      } else if (this.formulario.idadeMin) {
        filtros.push(`idade >= ${this.formulario.idadeMin}`)
      } else if (this.formulario.idadeMax) {
        filtros.push(`idade <= ${this.formulario.idadeMax}`)
      }

      // Número de compras
      if (this.formulario.minCompras && this.formulario.maxCompras) {
        filtros.push(`total_compras between (${this.formulario.minCompras} .. ${this.formulario.maxCompras})`)
      } else if (this.formulario.minCompras) {
        filtros.push(`total_compras >= ${this.formulario.minCompras}`)
      } else if (this.formulario.maxCompras) {
        filtros.push(`total_compras <= ${this.formulario.maxCompras}`)
      }

      // Valor gasto
      if (this.formulario.valorMinimo && this.formulario.valorMaximo) {
        filtros.push(`valor_total between (${this.formulario.valorMinimo} .. ${this.formulario.valorMaximo})`)
      } else if (this.formulario.valorMinimo) {
        filtros.push(`valor_total >= ${this.formulario.valorMinimo}`)
      } else if (this.formulario.valorMaximo) {
        filtros.push(`valor_total <= ${this.formulario.valorMaximo}`)
      }

      // Data de primeira compra
      if (this.formulario.dataInicioCompras && this.formulario.dataFimCompras) {
        filtros.push(`primeira_compra between (datetime(${this.formulario.dataInicioCompras}) .. datetime(${this.formulario.dataFimCompras}))`)
      } else if (this.formulario.dataInicioCompras) {
        filtros.push(`primeira_compra >= datetime(${this.formulario.dataInicioCompras})`)
      } else if (this.formulario.dataFimCompras) {
        filtros.push(`primeira_compra <= datetime(${this.formulario.dataFimCompras})`)
      }

      // Tipo de cliente
      if (this.formulario.tipoCliente) {
        filtros.push(`tipo_cliente == "${this.formulario.tipoCliente}"`)
      }

      kql += ` | where ${filtros.join(' and ')}`

      const limite = this.formulario.limite || 10
      kql += ` | take ${limite}`

      console.log('🔍 DEBUG - KQL Gerado:', kql)

      return kql
    },

    // Mantido para compatibilidade com visualizar/parse de source_kql salvo
    gerarKQLComLimite(limite) {
      let kql = 'clientes'
      const filtros = []
      filtros.push(`store_uuid == "${this.storeUuid}"`)
      if (this.formulario.genero) {
        filtros.push(`genero == "${this.formulario.genero}"`)
      }
      if (this.formulario.estados && this.formulario.estados.length > 0) {
        const { estados } = this.formulario
        if (estados.length === 1) {
          filtros.push(`estado == "${estados[0]}"`)
        } else if (estados.length > 1) {
          const estadosStr = estados.map(e => `"${e}"`).join(', ')
          filtros.push(`estado in (${estadosStr})`)
        }
      }
      kql += ` | where ${filtros.join(' and ')}`
      kql += ` | take ${limite}`
      console.log('🔍 DEBUG - KQL Simplificado:', kql)
      return kql
    },

    // Nova função para estruturar filtros trabalhando diretamente com JSON
    estruturarFiltrosJSON(audienciaData) {
      // Estrutura padrão dos filtros baseada na nova estrutura
      const filtros = {
        phone_required: true,
        genero: null,
        estado: [],
        idade: {},
        total_compras: {},
        valor_total: {},
        primeira_compra: {},
        tipo_cliente: null,
        take: 1000,
      }

      // Se os dados já vêm estruturados em JSON do backend
      if (audienciaData && typeof audienciaData === 'object') {
        try {
          // phone_required
          if (audienciaData.phone_required !== undefined) {
            filtros.phone_required = Boolean(audienciaData.phone_required)
          }

          // genero
          if (audienciaData.genero) {
            filtros.genero = audienciaData.genero
          }

          // estado
          if (audienciaData.estado) {
            // Se é array, usar diretamente; se é string, converter para array
            if (Array.isArray(audienciaData.estado)) {
              filtros.estado = audienciaData.estado
            } else if (typeof audienciaData.estado === 'string') {
              filtros.estado = [audienciaData.estado]
            }
          }

          // idade
          if (audienciaData.idade && typeof audienciaData.idade === 'object') {
            if (audienciaData.idade.min !== undefined) {
              filtros.idade.min = audienciaData.idade.min
            }
            if (audienciaData.idade.max !== undefined) {
              filtros.idade.max = audienciaData.idade.max
            }
          }

          // total_compras (mudou de total_pedidos)
          if (audienciaData.total_compras && typeof audienciaData.total_compras === 'object') {
            if (audienciaData.total_compras.min !== undefined) {
              filtros.total_compras.min = audienciaData.total_compras.min
            }
            if (audienciaData.total_compras.max !== undefined) {
              filtros.total_compras.max = audienciaData.total_compras.max
            }
          }

          // valor_total
          if (audienciaData.valor_total && typeof audienciaData.valor_total === 'object') {
            if (audienciaData.valor_total.min !== undefined) {
              filtros.valor_total.min = audienciaData.valor_total.min
            }
            if (audienciaData.valor_total.max !== undefined) {
              filtros.valor_total.max = audienciaData.valor_total.max
            }
          }

          // primeira_compra (novo campo)
          if (audienciaData.primeira_compra && typeof audienciaData.primeira_compra === 'object') {
            if (audienciaData.primeira_compra.inicio) {
              filtros.primeira_compra.inicio = audienciaData.primeira_compra.inicio
            }
            if (audienciaData.primeira_compra.fim) {
              filtros.primeira_compra.fim = audienciaData.primeira_compra.fim
            }
          }

          // tipo_cliente (novo campo)
          if (audienciaData.tipo_cliente) {
            filtros.tipo_cliente = audienciaData.tipo_cliente
          }

          // take (limite de contatos)
          if (audienciaData.take !== undefined) {
            filtros.take = parseInt(audienciaData.take)
          }
        } catch (error) {
          console.error('Erro ao estruturar filtros JSON:', error)
        }
      }

      return filtros
    },

    parseKQLParaFormulario(sourceKql) {
      console.log('🔍 Parsing KQL:', sourceKql)

      try {
        // Extrair take/limite
        const takeMatch = sourceKql.match(/\|\s*take\s+(\d+)/)
        if (takeMatch) this.formulario.limite = parseInt(takeMatch[1])

        // Buscar estados em TODO o KQL (pode estar dentro de join)
        // Estados - único
        const estadoUnicoMatch = sourceKql.match(/estado\s*==\s*"([^"]+)"/i)
        if (estadoUnicoMatch) this.formulario.estados = [estadoUnicoMatch[1]]

        // Estados - múltiplos (estado in ("ES", "MG", ...))
        const estadosMultiplosMatch = sourceKql.match(/estado\s+in\s*\(([^)]+)\)/i)
        if (estadosMultiplosMatch) {
          const estados = estadosMultiplosMatch[1].match(/"([^"]+)"/g)
          if (estados) {
            this.formulario.estados = estados.map(e => e.replace(/"/g, ''))
            console.log('🗺️ Estados encontrados:', this.formulario.estados)
          }
        }

        // Extrair todas as cláusulas where do KQL (pode ter múltiplas)
        const allWhereClauses = sourceKql.match(/\|\s*where\s+([^|]+)/g) || []
        const fullWhereClause = allWhereClauses.map(w => w.replace(/\|\s*where\s+/, '')).join(' and ')

        console.log('🔍 Where clauses encontradas:', fullWhereClause)

        // Gênero - busca em todo o KQL
        const generoMatch = sourceKql.match(/genero\s*==\s*"([MF])"/i)
        if (generoMatch) this.formulario.genero = generoMatch[1]

        // Idade
        const idadeBetweenMatch = sourceKql.match(/idade\s+between\s*\((\d+)\s*\.\.\s*(\d+)\)/i)
        if (idadeBetweenMatch) {
          this.formulario.idadeMin = idadeBetweenMatch[1]
          this.formulario.idadeMax = idadeBetweenMatch[2]
        } else {
          const idadeMinMatch = sourceKql.match(/idade\s*>=\s*(\d+)/i)
          if (idadeMinMatch) this.formulario.idadeMin = idadeMinMatch[1]
          const idadeMaxMatch = sourceKql.match(/idade\s*<=\s*(\d+)/i)
          if (idadeMaxMatch) this.formulario.idadeMax = idadeMaxMatch[1]
        }

        // Total de compras/pedidos
        const comprasBetweenMatch = fullWhereClause.match(/total_(?:compras|pedidos)\s+between\s*\((\d+)\s*\.\.\s*(\d+)\)/i)
        if (comprasBetweenMatch) {
          this.formulario.minCompras = comprasBetweenMatch[1]
          this.formulario.maxCompras = comprasBetweenMatch[2]
        } else {
          const comprasMinMatch = fullWhereClause.match(/total_(?:compras|pedidos)\s*>=\s*(\d+)/i)
          if (comprasMinMatch) this.formulario.minCompras = comprasMinMatch[1]
          const comprasMaxMatch = fullWhereClause.match(/total_(?:compras|pedidos)\s*<=\s*(\d+)/i)
          if (comprasMaxMatch) this.formulario.maxCompras = comprasMaxMatch[1]
        }

        // Valor total/mínimo/máximo
        const valorBetweenMatch = fullWhereClause.match(/valor_total\s+between\s*\(([0-9.]+)\s*\.\.\s*([0-9.]+)\)/i)
        if (valorBetweenMatch) {
          this.formulario.valorMinimo = valorBetweenMatch[1]
          this.formulario.valorMaximo = valorBetweenMatch[2]
        } else {
          const valorMinMatch = fullWhereClause.match(/valor_total\s*>=\s*([0-9.]+)/i)
          if (valorMinMatch) this.formulario.valorMinimo = valorMinMatch[1]
          const valorMaxMatch = fullWhereClause.match(/valor_total\s*<=\s*([0-9.]+)/i)
          if (valorMaxMatch) this.formulario.valorMaximo = valorMaxMatch[1]
        }

        // Data de compra (data_pedido ou primeira_compra) - busca em todo o KQL
        const dataInicioMatch = sourceKql.match(/(?:data_pedido|primeira_compra)\s*>=\s*datetime\(['""]?([^)'""]+)['""]?\)/i)
        if (dataInicioMatch) {
          const dataStr = dataInicioMatch[1].replace(/["']/g, '')
          this.formulario.dataInicioCompras = dataStr.split('T')[0]
        }

        const dataFimMatch = sourceKql.match(/(?:data_pedido|primeira_compra)\s*<=\s*datetime\(['""]?([^)'""]+)['""]?\)/i)
        if (dataFimMatch) {
          const dataStr = dataFimMatch[1].replace(/["']/g, '')
          this.formulario.dataFimCompras = dataStr.split('T')[0]
        }

        // Data between
        const dataBetweenMatch = sourceKql.match(/(?:data_pedido|primeira_compra)\s+between\s*\(datetime\(['""]?([^)'""]+)['""]?\)\s*\.\.\s*datetime\(['""]?([^)'""]+)['""]?\)\)/i)
        if (dataBetweenMatch) {
          this.formulario.dataInicioCompras = dataBetweenMatch[1].replace(/["']/g, '').split('T')[0]
          this.formulario.dataFimCompras = dataBetweenMatch[2].replace(/["']/g, '').split('T')[0]
        }

        // Tipo de cliente - busca em todo o KQL
        const tipoClienteMatch = sourceKql.match(/tipo_cliente\s*==\s*['"]([^'"]+)['"]/i)
        if (tipoClienteMatch) this.formulario.tipoCliente = tipoClienteMatch[1]

        console.log('📝 Filtros extraídos do KQL:', {
          genero: this.formulario.genero,
          estados: this.formulario.estados,
          idadeMin: this.formulario.idadeMin,
          idadeMax: this.formulario.idadeMax,
          minCompras: this.formulario.minCompras,
          maxCompras: this.formulario.maxCompras,
          valorMinimo: this.formulario.valorMinimo,
          valorMaximo: this.formulario.valorMaximo,
          dataInicioCompras: this.formulario.dataInicioCompras,
          dataFimCompras: this.formulario.dataFimCompras,
          tipoCliente: this.formulario.tipoCliente,
          limite: this.formulario.limite,
        })
      } catch (error) {
        console.error('❌ Erro no parsing do KQL:', error)
      }
    },
  },
}
</script>

<style scoped>
.audiencia-page {
  padding: 1.5rem;
}

.card-title {
  display: flex;
  align-items: center;
}

/* Cards hover effect */
.hover-card {
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

.hover-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
}

/* Estatísticas cards */
.shadow-sm {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;
}

/* Feather icons */
.feather {
  width: 16px;
  height: 16px;
}

/* Cursor pointer */
.cursor-pointer {
  cursor: pointer;
  transition: opacity 0.2s ease-in-out;
}

.cursor-pointer:hover {
  opacity: 0.7;
}

/* Loading overlay - estilo igual aos templates */
.modal-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
  border-radius: 0.5rem;
}

.loading-content {
  text-align: center;
  padding: 2rem;
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Badge personalizado */
.badge {
  font-size: 0.75rem;
}

/* Modal customizations */
.modal-lg {
  max-width: 900px;
}

/* Form customizations */
.form-control:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

/* Table customizations */
.table-sm th,
.table-sm td {
  padding: 0.5rem;
}

.bg-light {
  background-color: #f8f9fa !important;
}

/* Button group customizations */
.btn-group-sm > .btn {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .audiencia-page {
    padding: 1rem;
  }

  .d-flex.justify-content-between {
    flex-direction: column;
    gap: 1rem;
  }

  .d-flex.justify-content-between .d-flex {
    justify-content: center;
  }
}

/* Loading spinner */
.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

/* Status alerts in cards */
.alert.py-2 {
  font-size: 0.875rem;
  border-radius: 0.25rem;
}

.alert .feather {
  font-size: 1rem;
}

/* Text utilities */
.text-muted {
  color: #6c757d !important;
}

.text-primary {
  color: #007bff !important;
}

.text-success {
  color: #28a745 !important;
}

.text-warning {
  color: #ffc107 !important;
}

.text-info {
  color: #17a2b8 !important;
}

.text-danger {
  color: #dc3545 !important;
}

/* Card customizations */
.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.border-0 {
  border: 0 !important;
}

/* Preview section */
.bg-light.p-3.rounded {
  border: 1px solid #dee2e6;
}

/* Button customizations */
.btn-outline-primary:hover {
  color: #fff;
  background-color: #007bff;
  border-color: #007bff;
}

.btn-outline-secondary:hover {
  color: #fff;
  background-color: #6c757d;
  border-color: #6c757d;
}

.btn-outline-danger:hover {
  color: #fff;
  background-color: #dc3545;
  border-color: #dc3545;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

// Hover effect for cards - unscoped to work with Bootstrap Vue
.hover-shadow {
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out !important;
  cursor: pointer;
  will-change: transform, box-shadow;

  &:hover {
    transform: translateY(-4px) !important;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
  }
}
</style>
